import React, { useContext, startTransition, useActionState, useEffect, useState, useRef } from "react";
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import PageHeader from '@salesforce/design-system-react/components/page-header';
import PageHeaderControl from '@salesforce/design-system-react/components/page-header/control';
import Dropdown from './Dropdown';
import SearchText from "./SearchText";
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import { LocalizationContext } from "../context/LocalizationContext";
import { NotificationContext } from "../context/NotificationContext";
import { fetchSmartCaptureForms } from "../actions/fetchSmartCaptureForms";
import { SelectedFormContext } from "../context/SelectedFormContext";


/**
 * Translate the content type cell, we expect the "children" prop to be
 * a the CotnentType string. This could be moved to it's own file if this file
 * grows too large.
 */
export function ContentTypeCell({ children: contentType = '', ...props }) {
	const i18n = useContext(LocalizationContext);
	let contentTypeTranslated = contentType;
	switch (contentType.toLowerCase()) {
		case 'landingpage':
			contentTypeTranslated = i18n.get('EVENT_CHANNEL_LANDING_PAGE');
			break;
		case 'microsite':
			contentTypeTranslated = i18n.get('EVENT_CHANNEL_MICROSITE');
			break;
		case 'mobilepushpage':
			contentTypeTranslated = i18n.get('EVENT_CHANNEL_MOBILEPUSH_PAGE');
			break;
		default:
			contentTypeTranslated = contentType;
	}

	return (
		<DataTableCell {...props}>
			{contentTypeTranslated}
		</DataTableCell>
	);
}
ContentTypeCell.displayName = DataTableCell.displayName;

/**
 * Format the smartcapture form name cell to include the status below the name.
 * This could be moved to it's own file if this file grows too large.
 */
export function FormNameCell({ children: formName = '', item, ...props }) {
	const i18n = useContext(LocalizationContext);

	let pageStatus = item.pageStatus || 'draft';
	let pageStatusTranslated = item.pageStatus;
	switch (pageStatus.toLowerCase()) {
		case 'draft':
			pageStatusTranslated = i18n.get('EVENT_STATUS_DRAFT');
			break;
		case 'archived':
			pageStatusTranslated = i18n.get('EVENT_STATUS_ARCHIVED');
			break;
		case 'live':
		case 'published':
			pageStatusTranslated = i18n.get('EVENT_STATUS_LIVE');
			break;
		case 'scheduled':
			pageStatusTranslated = i18n.get('EVENT_STATUS_SCHEDULED');
			break;
		case 'unpublished':
			pageStatusTranslated = i18n.get('EVENT_STATUS_UNPUBLISHED');
			break;
		default:
			pageStatusTranslated = i18n.get('EVENT_STATUS_DRAFT');
	}

	return (
		<DataTableCell {...props}>
			<p>{formName}</p><p className="eventsourcelist-status">{pageStatusTranslated}</p>
		</DataTableCell>
	);
}
FormNameCell.displayName = DataTableCell.displayName;

function EventSourceList() {
	// Contexts
	const i18n = useContext(LocalizationContext);
	const { showErrorMessage } = useContext(NotificationContext);
	const { setFormData } = useContext(SelectedFormContext);

	// States
	const [channelType, setChannelType] = useState('0');
	const [search, setSearch] = useState('');
	const [hasMore, setHasMore] = useState(false);
	const [selectedItems, setSelectedItems] = useState([]);

	// Refs
	const page = useRef(1);
	const pageSize = useRef(10);

	const [items, fetchItems, isFetchPending] = useActionState(async (previousItems) => {

		let newItems = [];
		try {
			const response = await fetchSmartCaptureForms({
				page: page.current,
				pageSize: pageSize.current,
				channelTypeId: Number(channelType),
				search: search
			});
			if (response && response.entities) {
				if (page.current > 1) {
					newItems = newItems.concat(...previousItems);
				}
				newItems = newItems.concat(...response.entities);

				setHasMore(newItems.length < response.totalCount);
				page.current++;
			} else {
				setHasMore(false);
			}
		} catch {
			showErrorMessage(i18n.get('ERROR_MSG_LOADFORMS'));
		}

		return newItems;
	}, []);

	useEffect(() => {
		startTransition(() => {
			page.current = 1;
			fetchItems();
		});
	}, [channelType, search]);

	const handleLoadMore = () => {
		startTransition(() => {
			if (!isFetchPending) {
				fetchItems();
			}
		});
	};

	const handleRowSelection = (event, data) => {
		setFormData(data.selection[0]); // If no rows are selected this will set form data to undefined
		setSelectedItems(data.selection);
	};

	const isEmpty = items.length === 0;

	const headerControls = () => (
		<React.Fragment>
			<PageHeaderControl>
				<div className="eventsourcelist-headercontrol slds-grid slds-grid_pull-padded slds-grid_vertical-align-center">
					<div className="header-channeltype slds-col_padded">
						<Dropdown
							options={[{
								label: i18n.get('EVENT_FILTER_ALL'),
								value: '0'
							}, {
								label: i18n.get('EVENT_CHANNEL_LANDING_PAGE'),
								value: '3'
							}, {
								label: i18n.get('EVENT_CHANNEL_MICROSITE'),
								value: '4'
							}]}
							value={channelType}
							onChange={setChannelType}
							label={i18n.get('EVENT_LABEL_CONTENTTYPE')}
						>
						</Dropdown>
					</div>
					<div className="header-searchtext slds-col_padded">
						<SearchText
							value={search}
							onChange={setSearch}
						/>
					</div>
				</div>
			</PageHeaderControl>
		</React.Fragment>
	);

	return (
		<IconSettings iconPath="/assets/icons">
			<div className="slds-is-relative">
				<PageHeader
					onRenderControls={headerControls}
					varient="object-home"
					joined
				/>
				<DataTable
					items={items}
					id="SmartCaptureFormsDataTable"
					joined
					selectRows="radio"
					fixedHeader
					onLoadMore={handleLoadMore}
					hasMore={hasMore}
					selection={selectedItems}
					onRowChange={handleRowSelection}
				>
					<DataTableColumn
						label={i18n.get('SUMMARY_LABEL_FORM_NAME')}
						property="name"
						truncate
					>
						<FormNameCell />
					</DataTableColumn>
					<DataTableColumn
						label={i18n.get('EVENT_LABEL_PAGE_NAME')}
						property="pageName"
						truncate
					/>
					<DataTableColumn
						label={i18n.get('EVENT_LABEL_CONTENT_TYPE')}
						property="channelType"
						truncate
					>
						<ContentTypeCell />
					</DataTableColumn>
					<DataTableColumn
						label={i18n.get('EVENT_LABEL_FOLDER')}
						property="collectionName"
						truncate
					/>
				</DataTable>
			</div>
		</IconSettings>
	);
}

export default EventSourceList;